<script lang="ts" setup>
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Navigation} from "swiper/modules";
import 'swiper/css';
import {ArrowUpRightIcon, ArrowLeftIcon, ArrowRightIcon} from "@heroicons/vue/16/solid";
import ProjectHighlight from "~/components/project/ProjectHighlight.vue";

const {collectionUri, getPagePath} = useI18nCollection();
const {getIndex, getAllButIndex} = usePage();
const pages = await queryCollection(collectionUri('projects')).all();
const index = getIndex(pages)!;
const entries = getAllButIndex(pages)!;
const swiperInstance = ref();
const isBeginning = ref<boolean>(true);
const isEnd = ref<boolean>(false);
const onSwiper = (swiper: any) => {
    swiperInstance.value = swiper;
    refreshNavigationVisibility();
};
const slideNext = () => {
    swiperInstance.value?.slideNext();
};
const slidePrev = () => {
    swiperInstance.value?.slidePrev();
};

function refreshNavigationVisibility() {
    nextTick().then(() => {
        if (swiperInstance.value) {
            isBeginning.value = swiperInstance.value.isBeginning;
            isEnd.value = swiperInstance.value.isEnd;
        }
    });
}
</script>

<template>
  <section class="bg-secondary-light py-16 px-2 tablet:px-0">
    <div class="page-container">
      <header class="mb-10 border-t border-brand pt-6 px-4">
        <h2 class="section-title mb-6">{{ index.title }}</h2>
        <ButtonCta v-if="index?.meta?.cta"
                   :cta="index.meta.cta"
                   class="btn-brand"
        >
          <template #icon-end>
            <ArrowUpRightIcon class="size-6 ms-2"/>
          </template>
        </ButtonCta>
      </header>
    </div>
    <div class="mx-auto-left tablet:ps-6 desktop:ps-8 wide:ps-12 mb-8">
      <swiper
              :modules="[Navigation]"
              :pagination="{
                clickable: true,
            }"
              :slidesPerView="'auto'"
              :space-between="16"
              @swiper="onSwiper"
              @slide-change="refreshNavigationVisibility"
      >
        <swiper-slide
                v-for="item in entries"
                :key="item.path"
                class=""
        >
          <ProjectHighlight
                  :item="{
                        title: item.title,
                        slug: getPagePath(item.path),
                        hat: item!.meta!.hat,
                        category: item!.meta!.category,
                        meta: item.meta
                    }"
                  class="h-full w-[82vw] tablet:w-[33vw] desktop:w-[25vw]"
          />
        </swiper-slide>
      </swiper>
    </div>
    <div class="page-container flex justify-end gap-2">
      <button :disabled="isBeginning"
              class="disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-white disabled:text-brand disabled:translate-x-0 cursor-pointer p-3 bg-white hover:bg-brand text-brand hover:text-white transition-all hover:-translate-x-1 rounded-full"
              @click="slidePrev">
        <ArrowLeftIcon class="size-6"/>
      </button>
      <button :disabled="isEnd"
              class="disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-white disabled:text-brand disabled:translate-x-0 cursor-pointer p-3 bg-white hover:bg-brand text-brand hover:text-white transition-colors hover:translate-x-1 rounded-full"
              @click="slideNext">
        <ArrowRightIcon class="size-6"/>
      </button>
    </div>
  </section>
</template>

<style scoped>
@media (min-width: 1280px) {
    .mx-auto-left {
        margin-left: calc((100% - 80rem) / 2)
    }
}

@media (min-width: 1536px) {
    .mx-auto-left {
        margin-left: calc((100% - 96rem) / 2)
    }
}

:deep(.swiper-wrapper) {
    align-items: stretch;
}

:deep(.swiper-slide) {
    height: auto;
    width: auto;
}
</style>